import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Clock, Star, Trophy, CheckCircle, AlertCircle, HelpCircle } from 'lucide-react';
import { useScorekeeperData } from '@/hooks/useScorekeeperData';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import Header from '@/components/Header';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import Footer from '@/components/Footer';
import ScorekeeperNav from '@/components/ScorekeeperNav';

const ScorekeeperDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();
  
  const {
    performanceMetrics,
    achievements,
    activeSessions,
    loading,
    error,
    refreshData,
    userId
  } = useScorekeeperData();

  if (loading) {
    return (
      <>
        <div className="hidden md:block">
          <Header />
        </div>
        <div className="md:hidden">
          <MobileHeader />
        </div>
        <div className="min-h-screen bg-black flex items-center justify-center pt-20 pb-24 md:pb-8">
          <div className="text-white">Loading scorekeeper data...</div>
        </div>
        <MobileBottomNav />
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="hidden md:block">
          <Header />
        </div>
        <div className="md:hidden">
          <MobileHeader />
        </div>
        <div className="min-h-screen bg-black flex items-center justify-center pt-20 pb-24 md:pb-8">
          <div className="text-red-400">Error: {error}</div>
        </div>
        <div className="md:hidden">
          <MobileBottomNav />
        </div>
        <div className="hidden md:block">
          <Footer />
        </div>
      </>
    );
  }

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'big_player':
        return { label: 'Big Player', color: 'text-purple-400', bg: 'bg-purple-500/10' };
      case 'no_cap':
        return { label: 'No Cap', color: 'text-green-400', bg: 'bg-green-500/10' };
      case 'on_the_rise':
        return { label: 'On The Rise', color: 'text-blue-400', bg: 'bg-blue-500/10' };
      case 'qualified_scorekeeper':
        return { label: 'Qualified', color: 'text-yellow-400', bg: 'bg-yellow-500/10' };
      default:
        return { label: 'Bronze', color: 'text-orange-400', bg: 'bg-orange-500/10' };
    }
  };

  const statusInfo = getStatusInfo(performanceMetrics?.current_status || 'bronze');

  return (
    <>
      <div className="hidden md:block">
        <Header />
      </div>
      <div className="md:hidden">
        <MobileHeader />
      </div>
      <div className="min-h-screen bg-black pt-20 pb-24 md:pb-8">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Scorekeeper Dashboard</h1>
              <div className="flex items-center gap-3">
                <Badge className={`${statusInfo.bg} ${statusInfo.color} border-0`}>
                  {statusInfo.label}
                </Badge>
                {performanceMetrics?.big_player_eligible && (
                  <Badge className="bg-purple-500/10 text-purple-400 border-0">
                    Big Player Eligible
                  </Badge>
                )}
              </div>
            </div>
            <Button onClick={refreshData} variant="outline">
              Refresh Data
            </Button>
          </div>

          <ScorekeeperNav />

        <Tabs value={activeTab} className="space-y-6">
          {/* Custom Button Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full max-w-4xl mx-auto">
            <Button
              onClick={() => setActiveTab("overview")}
              variant={activeTab === "overview" ? "default" : "outline"}
              className="h-16 rounded-lg text-white bg-gray-800 hover:bg-gray-700 border-gray-600 transition-all duration-200"
            >
              <div className="flex flex-col items-center gap-1">
                <Star className="h-5 w-5" />
                <span className="text-sm font-medium">Overview</span>
              </div>
            </Button>
            
            <Button
              onClick={() => setActiveTab("active-games")}
              variant={activeTab === "active-games" ? "default" : "outline"}
              className="h-16 rounded-lg text-white bg-gray-800 hover:bg-gray-700 border-gray-600 transition-all duration-200"
            >
              <div className="flex flex-col items-center gap-1">
                <Clock className="h-5 w-5" />
                <span className="text-sm font-medium">Active Games</span>
              </div>
            </Button>
            
            <Button
              onClick={() => setActiveTab("applications")}
              variant={activeTab === "applications" ? "default" : "outline"}
              className="h-16 rounded-lg text-white bg-gray-800 hover:bg-gray-700 border-gray-600 transition-all duration-200"
            >
              <div className="flex flex-col items-center gap-1">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Applications</span>
              </div>
            </Button>
            
            <Button
              onClick={() => setActiveTab("performance")}
              variant={activeTab === "performance" ? "default" : "outline"}
              className="h-16 rounded-lg text-white bg-gray-800 hover:bg-gray-700 border-gray-600 transition-all duration-200"
            >
              <div className="flex flex-col items-center gap-1">
                <Trophy className="h-5 w-5" />
                <span className="text-sm font-medium">Performance</span>
              </div>
            </Button>
            
            <Button
              onClick={() => setActiveTab("achievements")}
              variant={activeTab === "achievements" ? "default" : "outline"}
              className="h-16 rounded-lg text-white bg-gray-800 hover:bg-gray-700 border-gray-600 transition-all duration-200"
            >
              <div className="flex flex-col items-center gap-1">
                <Calendar className="h-5 w-5" />
                <span className="text-sm font-medium">Achievements</span>
              </div>
            </Button>
            
            <Button
              onClick={() => navigate("/support")}
              variant="outline"
              className="h-16 rounded-lg text-white bg-gray-800 hover:bg-gray-700 border-gray-600 transition-all duration-200"
            >
              <div className="flex flex-col items-center gap-1">
                <HelpCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Support</span>
              </div>
            </Button>
          </div>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Games</p>
                      <p className="text-2xl font-bold text-white">
                        {performanceMetrics?.total_games_scored || 0}
                      </p>
                    </div>
                    <Star className="h-8 w-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Perfect Games</p>
                      <p className="text-2xl font-bold text-white">
                        {performanceMetrics?.perfect_games || 0}
                      </p>
                    </div>
                    <Trophy className="h-8 w-8 text-yellow-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Tri-Fecta Count</p>
                      <p className="text-2xl font-bold text-white">
                        {performanceMetrics?.tri_fecta_count || 0}
                      </p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Active Sessions</p>
                      <p className="text-2xl font-bold text-white">
                        {activeSessions?.length || 0}
                      </p>
                    </div>
                    <Clock className="h-8 w-8 text-purple-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Grade Distribution */}
            {performanceMetrics && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Grade Distribution</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-5 gap-4">
                    {['A', 'B', 'C', 'D', 'F'].map((grade) => {
                      const count = performanceMetrics[`games_with_${grade.toLowerCase()}_rating`] || 0;
                      const total = performanceMetrics.total_games_scored || 1;
                      const percentage = Math.round((count / total) * 100);
                      
                      return (
                        <div key={grade} className="text-center">
                          <div className="text-2xl font-bold text-white">{count}</div>
                          <div className="text-sm text-gray-400">Grade {grade}</div>
                          <div className="text-xs text-gray-500">{percentage}%</div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="active-games" className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Active Scoring Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                {activeSessions?.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400">No active scoring sessions</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeSessions?.map((session) => (
                      <div key={session.id} className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-white font-medium">{session.game_id || 'Game Session'}</h3>
                            <p className="text-gray-400 text-sm">
                              Status: Active
                            </p>
                          </div>
                          <Link 
                            to={`/games/${session.game_id}/score`}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                          >
                            Continue Scoring
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="applications" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Scorekeeper Applications</h2>
              <Link to="/scorekeeper/applications">
                <Button variant="outline">View All Applications</Button>
              </Link>
            </div>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">Application management coming soon</p>
                  <p className="text-gray-500 text-sm mt-2">
                    You'll be able to view and manage your scorekeeper applications here
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="performance" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Scoring Accuracy</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {performanceMetrics ? (
                    <>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-400">On-Time Score</span>
                          <span className="text-white">95%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Accuracy Score</span>
                          <span className="text-white">98%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Trust Score</span>
                          <span className="text-white">96%</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-400">No performance data available</p>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Game Statistics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {performanceMetrics ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Total Corrections</span>
                        <span className="text-white">{performanceMetrics.total_corrections || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Total Disputes</span>
                        <span className="text-white">{performanceMetrics.total_disputes || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Consecutive Perfect Games</span>
                        <span className="text-white">{performanceMetrics.consecutive_perfect_games || 0}</span>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-400">No statistics available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="achievements" className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Achievements</CardTitle>
              </CardHeader>
              <CardContent>
                {achievements?.length === 0 ? (
                  <div className="text-center py-8">
                    <Trophy className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400">No achievements earned yet</p>
                    <p className="text-gray-500 text-sm mt-2">
                      Keep scoring games to earn achievements!
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {achievements?.map((achievement) => (
                      <div key={achievement.id} className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex items-start gap-3">
                          {achievement.icon_url ? (
                            <img 
                              src={achievement.icon_url} 
                              alt={achievement.achievement_name}
                              className="w-12 h-12 rounded"
                            />
                          ) : (
                            <Trophy className="h-12 w-12 text-yellow-400" />
                          )}
                          <div className="flex-1">
                            <h3 className="text-white font-medium">{achievement.achievement_name}</h3>
                            <p className="text-gray-400 text-sm mt-1">{achievement.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge className="bg-yellow-500/10 text-yellow-400 border-0">
                                {achievement.points_awarded} points
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {format(new Date(achievement.earned_at), 'MMM dd, yyyy')}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        </div>
      </div>
      <div className="md:hidden">
        <MobileBottomNav />
      </div>
      <div className="hidden md:block">
        <Footer />
      </div>
    </>
  );
};

export default ScorekeeperDashboard;